import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';

const selectExpenseId = (expense) => expense.id || expense._id;
const expensesAdapter = createEntityAdapter({
	selectId: selectExpenseId,
	sortComparer: (a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at),
});

const initialState = expensesAdapter.getInitialState({
	loading: false,
	creating: false,
	updating: false,
	deleting: false,
	error: null,
	filter: 'all', // 'all', 'expenses', 'salaries', or categoryId
	selectedDate: null,
});

const expensesSlice = createSlice({
	name: 'expenses',
	initialState,
	reducers: {
		setExpenses: (state, action) => {
			expensesAdapter.setAll(state, action.payload || []);
			state.loading = false;
			state.error = null;
		},
		upsertExpenses: (state, action) => {
			expensesAdapter.upsertMany(state, action.payload || []);
		},
		upsertExpense: (state, action) => {
			expensesAdapter.upsertOne(state, action.payload);
		},
		removeExpense: (state, action) => {
			expensesAdapter.removeOne(state, action.payload);
		},
		clearExpenses: (state) => {
			expensesAdapter.removeAll(state);
		},
		setFilter: (state, action) => {
			state.filter = action.payload;
		},
		setSelectedDate: (state, action) => {
			state.selectedDate = action.payload;
		},
		setLoading: (state, action) => {
			state.loading = action.payload;
		},
		setCreating: (state, action) => {
			state.creating = action.payload;
		},
		setUpdating: (state, action) => {
			state.updating = action.payload;
		},
		setDeleting: (state, action) => {
			state.deleting = action.payload;
		},
		setError: (state, action) => {
			state.error = action.payload;
		},
	},
	extraReducers: (builder) => {
		// Handle async thunks from expenseThunks.js
		builder
			// Create expense
			.addMatcher(
				(action) => action.type?.startsWith('expenses/createExpense'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.creating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.creating = false;
						if (action.payload) {
							expensesAdapter.upsertOne(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.creating = false;
						state.error = action.error?.message || 'Failed to create expense';
					}
				}
			)
			// Update expense
			.addMatcher(
				(action) => action.type?.startsWith('expenses/updateExpense'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.updating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.updating = false;
						if (action.payload) {
							expensesAdapter.upsertOne(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.updating = false;
						state.error = action.error?.message || 'Failed to update expense';
					}
				}
			)
			// Delete expense
			.addMatcher(
				(action) => action.type?.startsWith('expenses/deleteExpense'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.deleting = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.deleting = false;
						// action.payload is the expenseId
						if (action.payload) {
							expensesAdapter.removeOne(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.deleting = false;
						state.error = action.error?.message || 'Failed to delete expense';
					}
				}
			)
			// Fetch expenses
			.addMatcher(
				(action) => action.type?.startsWith('expenses/fetchExpenses'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.loading = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.loading = false;
						if (action.payload) {
							expensesAdapter.setAll(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.loading = false;
						state.error = action.error?.message || 'Failed to fetch expenses';
					}
				}
			)
			// Fetch daily expenses
			.addMatcher(
				(action) => action.type?.startsWith('expenses/fetchDailyExpenses'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.loading = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.loading = false;
						if (action.payload) {
							// Handle both array and { data: [...] } formats
							const expenses = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
							expensesAdapter.setAll(state, expenses);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.loading = false;
						state.error = action.error?.message || 'Failed to fetch daily expenses';
					}
				}
			);
	},
});

export const {
	setExpenses,
	upsertExpenses,
	upsertExpense,
	removeExpense,
	clearExpenses,
	setFilter,
	setSelectedDate,
	setLoading,
	setCreating,
	setUpdating,
	setDeleting,
	setError,
} = expensesSlice.actions;

export default expensesSlice.reducer;

// Selectors
const selectExpensesState = (state) => state.expenses;

export const {
	selectAll: selectAllExpenses,
	selectById: selectExpenseById,
	selectIds: selectExpenseIds,
} = expensesAdapter.getSelectors(selectExpensesState);

export const selectFilteredExpenses = createSelector(
	[selectAllExpenses, (state) => state.expenses.filter],
	(expenses, filter) => {
		if (!filter || filter === 'all') return expenses;
		if (filter === 'expenses') {
			return expenses.filter((e) => e.category !== 'salary');
		}
		if (filter === 'salaries') {
			return expenses.filter((e) => e.category === 'salary');
		}
		// Filter by categoryId
		return expenses.filter((e) => String(e.categoryId || e.category_id) === String(filter));
	}
);

export const makeSelectExpensesByDate = (date) =>
	createSelector([selectAllExpenses], (expenses) =>
		expenses.filter((e) => {
			const expenseDate = new Date(e.date || e.created_at);
			const filterDate = new Date(date);
			return expenseDate.toDateString() === filterDate.toDateString();
		})
	);

export const makeSelectExpensesByBranch = (branchId) =>
	createSelector([selectAllExpenses], (expenses) =>
		expenses.filter((e) => String(e.branchId || e.branch_id) === String(branchId))
	);

export const selectExpensesLoading = (state) => state.expenses.loading;
export const selectExpensesCreating = (state) => state.expenses.creating;
export const selectExpensesUpdating = (state) => state.expenses.updating;
export const selectExpensesDeleting = (state) => state.expenses.deleting;
export const selectExpensesError = (state) => state.expenses.error;
